import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

function SectionHeader({ dot, title }) {
  return (
    <div style={{ padding: '14px 20px', borderBottom: '0.5px solid var(--border)', background: 'linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))', display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, boxShadow: `0 0 8px ${dot}` }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{title}</span>
    </div>
  );
}

function SpendRow({ label, value, accent, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
      <span style={{ color: 'var(--text2)' }}>{label}</span>
      <span style={{ fontWeight: bold ? 700 : 600, color: accent ? '#534AB7' : 'var(--text)' }}>{value}</span>
    </div>
  );
}

function sourceIcon(source) {
  if (source === 'Scraped') return 'ti-world-search';
  if (source === 'CSV Upload') return 'ti-file-upload';
  if (source === 'Manual') return 'ti-user-plus';
  return 'ti-dots';
}

function fmtDollar(val) {
  if (val == null || val === 0) return '—';
  return '$' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function CPATracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('flo_user') || '{}').token || '';
    fetch('/api/cpa/summary', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ padding: 40, color: 'var(--text3)', fontSize: 13, textAlign: 'center' }}>
      Loading CPA data...
    </div>
  );
  if (error) return (
    <div style={{ padding: 40, color: '#E05C5C', fontSize: 13 }}>Error: {error}</div>
  );
  if (!data) return null;

  const summaryCards = [
    { label: 'Cost Per Lead',        value: fmtDollar(data.cpl),        color: '#534AB7', icon: 'ti-user-plus'        },
    { label: 'Cost Per Appointment', value: fmtDollar(data.cpa),        color: '#1D9E75', icon: 'ti-calendar'          },
    { label: 'Cost Per Policy',      value: fmtDollar(data.cpp),        color: '#D85A30', icon: 'ti-file-description'  },
    { label: 'Total Spend',          value: fmtDollar(data.totalSpend), color: '#378ADD', icon: 'ti-coin'              },
  ];

  const cpaCalcs = [
    {
      label:   'Cost Per Lead',
      formula: `$${data.totalSpend.toFixed(2)} ÷ ${data.leadsThisMonth} leads`,
      value:   fmtDollar(data.cpl),
      color:   '#534AB7',
      empty:   data.leadsThisMonth === 0,
    },
    {
      label:   'Cost Per Appointment',
      formula: `$${data.totalSpend.toFixed(2)} ÷ ${data.apptsThisMonth} appts`,
      value:   fmtDollar(data.cpa),
      color:   '#1D9E75',
      empty:   data.apptsThisMonth === 0,
    },
    {
      label:   'Cost Per Policy',
      formula: `$${data.totalSpend.toFixed(2)} ÷ ${data.policiesThisMonth} policies`,
      value:   fmtDollar(data.cpp),
      color:   '#D85A30',
      empty:   data.policiesThisMonth === 0,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 900 }}>

      {/* ── Summary cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
        {summaryCards.map(c => (
          <div key={c.label} style={{ background: 'var(--bg2)', borderRadius: 16, border: '0.5px solid rgba(83,74,183,0.2)', padding: '20px', boxShadow: '0 0 20px rgba(83,74,183,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
              <i className={`ti ${c.icon}`} style={{ fontSize: 14, color: c.color }} aria-hidden="true" />
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.label}</div>
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* ── Spend breakdown + Results row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        <div style={{ background: 'var(--bg2)', borderRadius: 16, border: '0.5px solid rgba(83,74,183,0.3)', overflow: 'hidden', boxShadow: '0 0 30px rgba(83,74,183,0.08)' }}>
          <SectionHeader dot="#534AB7" title="Spend Breakdown — This Month" />
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SpendRow
              label={`Subscription (${data.plan} plan)`}
              value={`$${data.planCost}`}
              accent
            />
            <SpendRow
              label={`Credits (${data.smsThisMonth.toLocaleString()} SMS × $0.009)`}
              value={`$${data.creditsCost.toFixed(2)}`}
            />
            <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 10, marginTop: 2 }}>
              <SpendRow label="Total spend" value={`$${data.totalSpend.toFixed(2)}`} bold />
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--bg2)', borderRadius: 16, border: '0.5px solid rgba(29,158,117,0.3)', overflow: 'hidden', boxShadow: '0 0 30px rgba(29,158,117,0.06)' }}>
          <SectionHeader dot="#1D9E75" title="Results — This Month" />
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SpendRow label="New leads added"     value={data.leadsThisMonth}   accent />
            <SpendRow label="Appointments booked" value={data.apptsThisMonth}          />
            <SpendRow label="Policies logged"     value={data.policiesThisMonth}       />
          </div>
        </div>

      </div>

      {/* ── CPA calculations ── */}
      <div style={{ background: 'var(--bg2)', borderRadius: 16, border: '0.5px solid rgba(83,74,183,0.3)', overflow: 'hidden', boxShadow: '0 0 30px rgba(83,74,183,0.08)' }}>
        <SectionHeader dot="#534AB7" title="CPA Calculations" />
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {cpaCalcs.map(c => (
            <div key={c.label} style={{ padding: '16px', borderRadius: 12, background: 'var(--bg3)', border: '0.5px solid var(--border)' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{c.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, fontFamily: 'monospace' }}>{c.formula}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: c.empty ? 'var(--text3)' : c.color }}>
                {c.empty ? 'No data' : c.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Lead source breakdown ── */}
      <div style={{ background: 'var(--bg2)', borderRadius: 16, border: '0.5px solid rgba(83,74,183,0.3)', overflow: 'hidden', boxShadow: '0 0 30px rgba(83,74,183,0.08)' }}>
        <SectionHeader dot="#378ADD" title="Lead Source Breakdown" />
        {data.sourceBreakdown.length === 0 ? (
          <div style={{ padding: '28px 20px', color: 'var(--text3)', fontSize: 13, textAlign: 'center' }}>
            No leads yet — add leads to see source breakdown.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg3)' }}>
                  {['Source', 'All-time leads', '% of total', 'Est. CPL (blended)'].map(h => (
                    <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontWeight: 600, color: 'var(--text2)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '0.5px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.sourceBreakdown.map((row, i) => (
                  <tr key={row.source} style={{ borderBottom: i < data.sourceBreakdown.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
                    <td style={{ padding: '12px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className={`ti ${sourceIcon(row.source)}`} style={{ fontSize: 14, color: '#534AB7' }} aria-hidden="true" />
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.source}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 20px', color: 'var(--text)', fontWeight: 600 }}>
                      {row.leads.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 80, height: 5, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
                          <div style={{ width: row.pct + '%', height: '100%', background: 'linear-gradient(90deg,#534AB7,#1D9E75)', borderRadius: 3 }} />
                        </div>
                        <span style={{ color: 'var(--text2)', fontSize: 12, minWidth: 30 }}>{row.pct}%</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 20px', fontWeight: 700, color: '#534AB7' }}>
                      {fmtDollar(data.cpl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: '8px 20px 12px', fontSize: 11, color: 'var(--text3)' }}>
              Est. CPL uses this month's blended rate (total spend ÷ total leads). Per-source attribution requires spend tracking by source.
            </div>
          </div>
        )}
      </div>

      {/* ── Monthly trend chart ── */}
      <div style={{ background: 'var(--bg2)', borderRadius: 16, border: '0.5px solid rgba(83,74,183,0.3)', overflow: 'hidden', boxShadow: '0 0 30px rgba(83,74,183,0.08)' }}>
        <SectionHeader dot="#7F77DD" title="Monthly CPA Trend — Last 6 Months" />
        <div style={{ padding: '20px 20px 12px' }}>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={data.monthlyTrend} margin={{ top: 5, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="month"
                tick={{ fill: 'var(--text3)', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'var(--text3)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v != null ? '$' + v : ''}
                width={52}
              />
              <Tooltip
                contentStyle={{ background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 10, fontSize: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                labelStyle={{ color: 'var(--text)', fontWeight: 700, marginBottom: 4 }}
                formatter={(value, name) => [
                  value != null ? '$' + value.toFixed(2) : '—',
                  name === 'cpl' ? 'Cost / Lead' : 'Cost / Appt',
                ]}
              />
              <Legend
                formatter={n => n === 'cpl' ? 'Cost / Lead' : 'Cost / Appt'}
                wrapperStyle={{ fontSize: 12, paddingTop: 12, color: 'var(--text2)' }}
              />
              <Line
                type="monotone"
                dataKey="cpl"
                stroke="#534AB7"
                strokeWidth={2}
                dot={{ fill: '#534AB7', r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="cpa"
                stroke="#1D9E75"
                strokeWidth={2}
                dot={{ fill: '#1D9E75', r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
}
